import type { VisitPopulated } from '@api/READ_ONLY/visits_api/Api';

/**
 * Measurements for the doctor's Overview, judged against the patient's own history.
 *
 * No universal bounds: systolic 165 is a crisis for one patient and a Tuesday for
 * another, which is why the threshold feature is switched off. Every value here is
 * compared to the median of that same patient's earlier readings of that same metric,
 * so nobody has to tune anything.
 *
 * Pure. The caller fetches the COMPLETED visits of the 180-day baseline window and
 * passes them in, in any order.
 */

export type MetricKey = 'systolic' | 'diastolic' | 'heartRate' | 'spo2' | 'temperature' | 'glucose' | 'fvc' | 'fev1';

/** The column on `visits` each metric is stored in. The same columns readVisitVitals reads. */
const METRIC_FIELDS: Record<MetricKey, keyof VisitPopulated> = {
  systolic: 'bloodPressureSystolic',
  diastolic: 'bloodPressureDiastolic',
  heartRate: 'heartRate',
  spo2: 'oxygenSaturation',
  temperature: 'temperature',
  glucose: 'bloodGlucose',
  fvc: 'fvc',
  fev1: 'fev1',
};

const METRIC_KEYS = Object.keys(METRIC_FIELDS) as MetricKey[];

/**
 * How far from the patient's own normal counts as a change. These are a clinical
 * product decision awaiting sign-off, so they sit in one exported table a reviewer can
 * change in one edit. SpO2 is down-only — a rise in saturation is not a finding.
 */
export const MOVEMENT_DELTAS: Record<MetricKey, { delta: number; direction: 'both' | 'down' }> = {
  systolic: { delta: 20, direction: 'both' },
  diastolic: { delta: 10, direction: 'both' },
  heartRate: { delta: 20, direction: 'both' },
  spo2: { delta: 3, direction: 'down' },
  temperature: { delta: 0.8, direction: 'both' },
  glucose: { delta: 50, direction: 'both' },
  // Decline-only, like SpO2: a rise in lung volume is not a finding. 0.20 L is the
  // ATS/ERS reversibility threshold. The derived ratio has no entry - flagging it
  // beside its own two inputs would report one event up to three times.
  fvc: { delta: 0.2, direction: 'down' },
  fev1: { delta: 0.2, direction: 'down' },
};

/** Below this many earlier readings we do not claim to know the patient's norm. */
const MIN_BASELINE_READINGS = 3;

/**
 * The boundary is inclusive and several deltas are decimals: 36.8 - 36.0 is
 * 0.7999999999999972 in binary floating point and would fail a bare `>= 0.8`.
 */
const EPSILON = 1e-9;

export type MetricAssessment = {
  metric: MetricKey;
  value: number;
  /** ISO, the startTime of the visit the value came from. */
  at: string;
  visitId: number;
  /** Median of at least 3 earlier readings of this metric, else null. */
  baseline: number | null;
  /** null when there is no baseline, or the value is within the delta. */
  moved: 'up' | 'down' | null;
};

export type PatientMeasurements = {
  clientId: number;
  /** The newest reading's `at` on this row — what the row is dated and sorted by. */
  latestAt: string;
  metrics: MetricAssessment[];
};

type Reading = { value: number; at: string; ts: number; visitId: number };

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Median, not mean: with the 3-15 points a patient actually has, one mistyped reading
 * drags a mean far enough to either invent a movement or hide one.
 */
const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const assess = (metric: MetricKey, readings: Reading[]): MetricAssessment | null => {
  if (readings.length === 0) return null;

  // Ascending, so the last one is the value being judged and everything strictly
  // earlier than it is its history. Equal timestamps are not "earlier".
  const ordered = [...readings].sort((a, b) => a.ts - b.ts || a.visitId - b.visitId);
  const latest = ordered[ordered.length - 1];
  const earlier = ordered.filter((reading) => reading.ts < latest.ts);

  const base: MetricAssessment = {
    metric,
    value: latest.value,
    at: latest.at,
    visitId: latest.visitId,
    baseline: null,
    moved: null,
  };
  if (earlier.length < MIN_BASELINE_READINGS) return base;

  const baseline = median(earlier.map((reading) => reading.value));
  const { delta, direction } = MOVEMENT_DELTAS[metric];
  const drop = baseline - latest.value;
  const distance = direction === 'down' ? drop : Math.abs(drop);
  const moved = distance >= delta - EPSILON ? (drop > 0 ? 'down' : 'up') : null;

  return { ...base, baseline, moved };
};

/**
 * @param visits COMPLETED visits across ALL the doctor's patients, inside the
 *               180-day baseline window, in any order.
 * @param displayFrom ISO instant: only patients with a reading at/after this
 *                    are returned (the 30-day display window).
 *
 * The window gates what is SHOWN, never what a baseline is computed from: a metric
 * whose newest reading predates it is left off the row, but every reading passed in
 * still counts towards the baselines of the metrics that stay.
 */
export const buildMeasurementRows = (visits: VisitPopulated[], displayFrom: string): PatientMeasurements[] => {
  const fromTs = Date.parse(displayFrom);
  const byClient = new Map<number, Map<MetricKey, Reading[]>>();

  for (const visit of visits) {
    const clientId = visit.client?.clientId;
    const ts = Date.parse(visit.startTime);
    if (typeof clientId !== 'number' || !Number.isFinite(ts)) continue;

    for (const metric of METRIC_KEYS) {
      const value = numberOrNull(visit[METRIC_FIELDS[metric]]);
      if (value === null) continue;

      let metrics = byClient.get(clientId);
      if (!metrics) {
        metrics = new Map<MetricKey, Reading[]>();
        byClient.set(clientId, metrics);
      }
      const readings = metrics.get(metric) ?? [];
      readings.push({ value, at: visit.startTime, ts, visitId: visit.id });
      metrics.set(metric, readings);
    }
  }

  const inWindow = (at: string): boolean => !Number.isFinite(fromTs) || Date.parse(at) >= fromTs;

  const rows: PatientMeasurements[] = [];
  for (const [clientId, metrics] of byClient) {
    const assessed = METRIC_KEYS.map((metric) => assess(metric, metrics.get(metric) ?? [])).filter(
      // A metric last measured in June under a row dated 18/09 reads as current. The
      // baseline behind the metrics that survive is untouched by this.
      (assessment): assessment is MetricAssessment => assessment !== null && inWindow(assessment.at)
    );
    // No in-window metric is no row: callers reduce over `metrics` to find the newest
    // reading, and an empty array throws.
    if (assessed.length === 0) continue;

    const latest = assessed.reduce((newest, current) =>
      Date.parse(current.at) > Date.parse(newest.at) ? current : newest
    );
    rows.push({ clientId, latestAt: latest.at, metrics: assessed });
  }

  return rows.sort((a, b) => Date.parse(b.latestAt) - Date.parse(a.latestAt) || a.clientId - b.clientId);
};
